import React, { useEffect, useState } from "react";
import { Check, Clock, Target } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const STATUS_META = {
  not_yet: { label: "NOT YET", icon: Target, color: "#FFC107" },
  on_the_route: { label: "ON THE ROUTE", icon: Clock, color: "#FF1E1E" },
  done: { label: "DONE", icon: Check, color: "#22C55E" },
};

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

/**
 * BucketListItems — public list, grouped by status.
 */
export const BucketListItems = ({ limit, filterStatus }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/bucket-list", { params: filterStatus ? { status: filterStatus } : {} })
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [filterStatus]);

  if (loading) {
    return (
      <div className="text-center font-mono-tactical text-[11px] uppercase tracking-widest text-zinc-500 py-6">
        Loading bucket list…
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div
        className="flex flex-col items-center gap-4 py-10 text-center"
        data-testid="bucket-list-empty"
      >
        <div className="stamp-not-yet">LIST STARTS SOON</div>
        <p className="text-zinc-400 max-w-md">
          BooBoo is still writing the bucket list. Suggest something to go on it below.
        </p>
      </div>
    );
  }

  const trimmed = limit ? items.slice(0, limit) : items;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="bucket-list-items">
      {trimmed.map((item) => {
        const meta = STATUS_META[item.status] || STATUS_META.not_yet;
        const Icon = meta.icon;
        return (
          <div
            key={item.id}
            className="tac-card p-4 flex gap-4"
            data-testid={`bucket-item-${item.id}`}
          >
            <div
              className="shrink-0 w-10 h-10 flex items-center justify-center border"
              style={{ borderColor: meta.color, color: meta.color }}
            >
              <Icon size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h4 className="font-display text-lg text-white uppercase tracking-tight leading-tight">
                  {item.title}
                </h4>
                {item.state_code && (
                  <span className="font-mono-tactical text-[10px] px-1.5 py-0.5 border border-white/15 text-zinc-400 shrink-0">
                    {item.state_code}
                  </span>
                )}
              </div>
              {item.description && (
                <p className="text-sm text-zinc-400 mt-1 line-clamp-2">{item.description}</p>
              )}
              <div className="flex items-center gap-2 mt-2">
                <span
                  className="font-mono-tactical text-[10px] uppercase tracking-widest"
                  style={{ color: meta.color }}
                >
                  {meta.label}
                </span>
                {item.is_community_suggested && (
                  <span className="font-mono-tactical text-[10px] uppercase tracking-widest text-zinc-500">
                    · suggested by {item.suggested_by || "a fan"}
                  </span>
                )}
                {item.status === "done" && item.state_code && (
                  <a
                    href={`#the-journey`}
                    className="font-mono-tactical text-[10px] uppercase tracking-widest text-emerald-400 hover:underline"
                    data-testid={`bucket-item-${item.id}-state-link`}
                  >
                    · {item.state_code} journey
                  </a>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * BucketListSuggestionForm — public submission form.
 */
export const BucketListSuggestionForm = () => {
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/community/bucket-list-suggestion", form);
      setSubmitted(true);
      toast.success("On BooBoo's radar.");
      setForm({});
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something broke. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="tac-card p-5 flex flex-col gap-3"
        data-testid="bucket-list-form-success"
      >
        <div className="road-sticker self-start">ADDED TO THE PILE</div>
        <h3 className="font-display text-2xl uppercase">BooBoo will decide.</h3>
        <p className="text-sm text-zinc-400">
          If it earns a spot, you'll see it on the public list.
        </p>
        <button
          type="button"
          onClick={() => setSubmitted(false)}
          className="btn-ghost self-start text-[11px]"
          data-testid="bucket-list-form-new"
        >
          + Suggest another
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="tac-card p-5 flex flex-col gap-3"
      data-testid="bucket-list-suggestion-form"
    >
      <div>
        <h3 className="font-display text-2xl uppercase tracking-tight leading-none">
          SUGGEST A BUCKET LIST ITEM
        </h3>
        <p className="text-xs font-mono-tactical text-zinc-500 mt-2 uppercase tracking-widest">
          An experience, a destination, a weird roadside thing BooBoo should hit.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input
          type="text"
          placeholder="Your name"
          className="tac-input"
          value={form.submitter_name || ""}
          onChange={set("submitter_name")}
          required
          data-testid="bucket-form-name"
        />
        <input
          type="email"
          placeholder="Email"
          className="tac-input"
          value={form.submitter_email || ""}
          onChange={set("submitter_email")}
          required
          data-testid="bucket-form-email"
        />
      </div>
      <input
        type="text"
        placeholder="The thing itself (e.g. Swim in the Great Salt Lake)"
        className="tac-input"
        value={form.title || ""}
        onChange={set("title")}
        required
        data-testid="bucket-form-title"
      />
      <select
        className="tac-input"
        value={form.state_code || ""}
        onChange={set("state_code")}
        data-testid="bucket-form-state"
      >
        <option value="">State (optional)</option>
        {US_STATES.map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>
      <textarea
        placeholder="Why does this belong on the list?"
        className="tac-input"
        rows={3}
        value={form.why || ""}
        onChange={set("why")}
        required
        data-testid="bucket-form-why"
      />
      <button
        type="submit"
        disabled={submitting}
        className="btn-blaze self-start"
        data-testid="bucket-form-submit"
      >
        {submitting ? "SENDING..." : "SEND SUGGESTION"}
      </button>
    </form>
  );
};
